import { useAppSelector } from '@common/redux/hooks';
import { getCollectionSelector } from '@common/redux/selectors/database';
import { getListOptions } from '@common/services';
import { IObj, ISelect } from '@common/types/';
import { AxiosResponse } from 'axios';
import { SimpleField } from 'click-types';
import { ceil, find, get, isEmpty, isNil, map } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const FIND_ALL = 'FIND_ALL';
const dateFormat = 'YYYY/MM/DD HH:mm'; // TODO: get from attributes
const dateOnlyFormat = 'YYYY/MM/DD'; // TODO: get from attributes

const useDropdown = (attributes: ISelect) => {
  const { changeInput, valueInput, dependencies } = attributes;
  const [listOption, setListOption] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [isLoadMore, setLoadMore] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);

  const pageSize = get(attributes, 'field.pageSize', 100);
  const totalPage = useMemo(() => ceil(total / pageSize), [total]);

  const onLoadMore = () => {
    if (page < totalPage) {
      setPage((preState) => preState + 1);
      setLoadMore(true);
    }
  };

  const getLabelField = get(attributes, 'database.options.labelField');
  const isMakeCustom = get(attributes, 'seperate.enabled', false);
  const databaseUuid = get(attributes, 'database.source.tableId');

  const currentTable = useAppSelector(
    getCollectionSelector(databaseUuid || '')
  );

  const getFieldSelected = useCallback(() => {
    return currentTable
      ? find(currentTable?.fields, (item) => item.fid === getLabelField)
      : null;
  }, [currentTable]);

  const handleShowDropdown = () => setShowDropDown(true);
  const handleHiddenDropdown = () => setShowDropDown(false);

  const optionDataOfDatabase = useCallback(() => {
    const defaultValue =
      get(attributes, 'attributes.defaultValue.text') || getLabelField;
    const fieldSelected = getFieldSelected();

    return map(listOption, (value: any) => {
      let label = value?.[defaultValue];
      if (label) {
        if (fieldSelected?.type === SimpleField.DATE_TIME) {
          label = moment(label).format(dateFormat);
        } else if (fieldSelected?.type === SimpleField.DATE_ONLY) {
          label = moment(label).format(dateOnlyFormat);
        }
      }
      return {
        value: value._id || value.id,
        label,
      };
    }).filter(
      (item: Record<string, any>) => !isNil(item.label) || !isEmpty(item.label)
    );
  }, [listOption, getFieldSelected]);

  const optionCustomData = useMemo(() => {
    return get(attributes, 'seperate.items', '')
      .split('\n')
      .map((item: string) => ({
        label: item.trim(),
        value: item.trim(),
      }))
      .filter(
        (item: Record<string, any>) =>
          !isNil(item.label) || !isEmpty(item.label)
      );
  }, [attributes]);

  const list = useMemo((): Array<{ value: string; label: string }> => {
    return isMakeCustom ? optionCustomData : optionDataOfDatabase();
  }, [optionCustomData, optionDataOfDatabase]);

  const handleChangeValue = (value: any) => {
    changeInput(value);
  };

  useEffect(() => {
    const getListOption = async (field: IObj) => {
      const params = {
        appId: dependencies.appInfor.id,
        fieldId: field?.fieldId,
        screenUuid: attributes?.screenUuid,
        objectId: attributes?.formId,
        dependencies,
        limit: pageSize * page,
      };

      getListOptions(params)
        .then((resp: AxiosResponse) => {
          setListOption(resp.data);
          setTotal(get(resp, 'total'));
          if (get(resp, 'data.length') == total) {
            setLoadMore(false);
          }
        })
        .catch(() => {
          setListOption([]);
        });
    };

    if (attributes?.formId && attributes?.field && dependencies?.appInfor) {
      getListOption(attributes?.field);
    } else {
      setListOption(attributes.dataBinding || []);
    }
  }, [
    attributes?.formId,
    attributes?.field,
    dependencies?.appInfor,
    attributes.dataBinding,
    page,
  ]);

  return {
    showDropDown,
    value: valueInput,
    list: list || [],
    handleShowDropdown,
    handleHiddenDropdown,
    handleChangeValue,
    onLoadMore,
    isLoadMore,
  };
};

export type useDropdownProps = ReturnType<typeof useDropdown>;
export default useDropdown;
